import React, { useState, useEffect } from 'react';
import { supabaseClient } from '../../services/supabase';
import { getTodayISO, formatDateForDisplay } from '../../utils/dateUtils';
import EditReservationModal from './EditReservationModal';
import AddReservationModal from './AddReservationModal';
import DatePicker from '../Common/DatePicker';

function ReservationList() {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDateStart, setFilterDateStart] = useState(getTodayISO());
  const [filterDateEnd, setFilterDateEnd] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingReservation, setEditingReservation] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    loadReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadReservations = async () => {
    setLoading(true);
    try {
      let query = supabaseClient
        .from('reservations')
        .select('*')
        .order('reservation_date', { ascending: true })
        .order('reservation_time', { ascending: true });

      // 初期表示は当日以降の予約
      if (!filterDateStart && !filterDateEnd) {
        const today = getTodayISO();
        query = query.gte('reservation_date', today);
      } else {
        if (filterDateStart) {
          query = query.gte('reservation_date', filterDateStart);
        }
        if (filterDateEnd) {
          query = query.lte('reservation_date', filterDateEnd);
        }
      }

      if (filterStatus) {
        query = query.eq('status', filterStatus);
      }
      if (searchQuery) {
        query = query.or(
          `customer_name.ilike.%${searchQuery}%,customer_phone.ilike.%${searchQuery}%,customer_email.ilike.%${searchQuery}%`
        );
      }

      const { data, error } = await query;

      if (error) throw error;
      setReservations(data || []);
    } catch (error) {
      console.error('予約読み込みエラー:', error);
      alert('予約データの読み込みに失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  const statusLabels = {
    pending: '店舗確認中',
    confirmed: '確定',
    cancelled: 'キャンセル',
    visited: '来店済み',
    no_show: '無断キャンセル',
  };

  return (
    <section className="admin-section">
      <div className="container">
        <div className="section-header">
          <h2>予約管理(一覧管理)</h2>
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            新規予約追加
          </button>
        </div>

        <div className="filter-section">
          <div className="filter-group">
            <label>日付範囲（開始日）</label>
            <DatePicker
              value={filterDateStart}
              onChange={(date) => setFilterDateStart(date)}
              placeholder="開始日を選択"
            />
          </div>
          <div className="filter-group">
            <label>日付範囲（終了日）</label>
            <DatePicker
              value={filterDateEnd}
              onChange={(date) => setFilterDateEnd(date)}
              placeholder="終了日を選択"
            />
          </div>
          <div className="filter-group">
            <label>ステータス</label>
            <select
              id="filterStatusList"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">すべて</option>
              <option value="pending">店舗確認中</option>
              <option value="confirmed">確定</option>
              <option value="cancelled">キャンセル</option>
              <option value="visited">来店済み</option>
              <option value="no_show">無断キャンセル</option>
            </select>
          </div>
          <div className="filter-group">
            <label>検索</label>
            <input
              type="text"
              id="searchQueryList"
              placeholder="名前、電話番号、メールアドレス"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button className="btn-primary" onClick={loadReservations}>
            検索
          </button>
        </div>

        {loading ? (
          <div>読み込み中...</div>
        ) : (
          <div className="reservations-table-container">
            <table className="reservations-table">
              <thead>
                <tr>
                  <th>予約日時</th>
                  <th>お名前</th>
                  <th>電話番号</th>
                  <th>メールアドレス</th>
                  <th>人数</th>
                  <th>席種</th>
                  <th>ステータス</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {reservations.length === 0 ? (
                  <tr>
                    <td colSpan="9">予約がありません</td>
                  </tr>
                ) : (
                  reservations.map((reservation) => (
                    <tr key={reservation.id}>
                      <td>
                        {formatDateForDisplay(reservation.reservation_date)}{' '}
                        {reservation.reservation_time}
                      </td>
                      <td>{reservation.customer_name || '-'}</td>
                      <td>{reservation.customer_phone || '-'}</td>
                      <td>{reservation.customer_email || '-'}</td>
                      <td>
                        大人{reservation.adult_count}名、子供
                        {reservation.child_count}名
                      </td>
                      <td>
                        {reservation.seat_type === 'counter'
                          ? 'カウンター'
                          : 'テーブル'}
                      </td>
                      <td>
                        <span
                          className={`status-badge status-${reservation.status}`}
                        >
                          {statusLabels[reservation.status] || reservation.status}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn-edit"
                          onClick={() => {
                            setEditingReservation(reservation);
                          }}
                        >
                          編集
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {editingReservation && (
          <EditReservationModal
            reservation={editingReservation}
            onClose={() => setEditingReservation(null)}
            onUpdate={() => {
              loadReservations();
            }}
          />
        )}

        {showAddModal && (
          <AddReservationModal
            onClose={() => setShowAddModal(false)}
            onAdd={() => {
              loadReservations();
            }}
          />
        )}
      </div>
    </section>
  );
}

export default ReservationList;

